import { useState } from 'react';
import {
  messages,
  type Estimate,
  type Farm,
  type Grower,
  type MasterData,
} from '@fruitways/shared';
import { EmptyState, FilterCard, Picker, SkeletonRows, type PickerOption } from '@/components';
import { useAdminFilters } from '@/app/AdminFilters';
import { EstimateRow } from './EstimateRow';

const t = messages.admin;

interface FarmBlock {
  farm: Farm;
  rows: Estimate[];
}
interface GrowerBlock {
  key: string;
  label: string;
  farmCount: number;
  farms: FarmBlock[];
}

/**
 * Estimates grouped by grower, then farm. With no filter it shows the farms that have
 * estimates; with a grower chosen it shows every farm of that grower, so the admin can see
 * who has not sent an estimate. `limit` caps rows per farm (Home shows the latest 2).
 */
export function groupForAdmin(
  estimates: readonly Estimate[],
  farms: readonly Farm[],
  growers: readonly Grower[],
  filter: { growerId: string | null; farmId: string | null },
  limit = 0,
): GrowerBlock[] {
  const filtered = Boolean(filter.growerId || filter.farmId);
  const rowsFor = (farmId: string) => {
    const rows = estimates
      .filter((e) => e.farmId === farmId)
      .sort((a, b) => (a.weekStart < b.weekStart ? 1 : -1));
    return limit ? rows.slice(0, limit) : rows;
  };
  const keep = (f: Farm) =>
    (!filter.farmId || f.id === filter.farmId) &&
    (filtered || estimates.some((e) => e.farmId === f.id));

  const out: GrowerBlock[] = [];
  for (const g of growers) {
    if (filter.growerId && g.id !== filter.growerId) continue;
    const own = farms.filter((f) => g.farmIds.includes(f.id));
    const shown = own.filter(keep);
    if (!shown.length) continue;
    out.push({
      key: g.id,
      label: g.displayName,
      farmCount: own.length,
      farms: shown.map((farm) => ({ farm, rows: rowsFor(farm.id) })),
    });
  }
  if (!filter.growerId) {
    // Farms no grower is linked to still show, so no estimate is ever hidden from the admin.
    const linked = new Set(growers.flatMap((g) => g.farmIds));
    const loose = farms.filter((f) => !linked.has(f.id) && keep(f));
    if (loose.length) {
      out.push({
        key: 'unlinked',
        label: t.home.unlinked,
        farmCount: loose.length,
        farms: loose.map((farm) => ({ farm, rows: rowsFor(farm.id) })),
      });
    }
  }
  return out;
}

export function AdminEstimates({
  master,
  estimates,
  growers,
  mode,
}: {
  master: MasterData | undefined;
  estimates: Estimate[] | undefined;
  growers: Grower[] | undefined;
  mode: 'recent' | 'all';
}) {
  const filters = useAdminFilters();
  const [picker, setPicker] = useState<'grower' | 'farm' | null>(null);
  const loading = !master || !estimates || !growers;
  const farms = master?.farms ?? [];
  const grower = growers?.find((g) => g.id === filters.growerId);
  const farm = farms.find((f) => f.id === filters.farmId);
  const growerFarms = grower ? farms.filter((f) => grower.farmIds.includes(f.id)) : farms;
  const nameOfGrower = (farmId: string) =>
    growers?.find((g) => g.farmIds.includes(farmId))?.displayName ?? '';

  const blocks =
    loading || !growers || !estimates
      ? []
      : groupForAdmin(estimates, farms, growers, filters, mode === 'recent' ? 2 : 0);
  const shownFarms = blocks.flatMap((b) => b.farms);
  const quiet = shownFarms.filter((b) => b.rows.length === 0).length;
  const quietAll = farms.filter((f) => !estimates?.some((e) => e.farmId === f.id)).length;

  const who = farm ? farm.name : grower ? t.home.whoGrower(grower.displayName) : t.home.whoEachFarm;
  const scope = mode === 'recent' ? t.home.scopeRecent(who) : t.home.scopeAll(who);
  const extra =
    !filters.growerId && !filters.farmId
      ? quietAll
        ? ` ${t.home.quietAll(quietAll, farms.length)}`
        : ''
      : shownFarms.length > 1 && quiet
        ? ` ${t.home.quietGrower(quiet, shownFarms.length)}`
        : '';

  const growerOptions: PickerOption<string | null>[] = [
    { value: null, label: t.filter.allGrowers, detail: t.filter.growers(growers?.length ?? 0) },
    ...(growers ?? []).map((g) => {
      const own = farms.filter((f) => g.farmIds.includes(f.id));
      return {
        value: g.id,
        label: g.displayName,
        detail: t.filter.growerDetail(own.length, own.map((f) => f.name).join(', ')),
      };
    }),
  ];
  const farmOptions: PickerOption<string | null>[] = [
    {
      value: null,
      label: t.filter.allFarms,
      detail: t.filter.allFarmsDetail(
        grower ? grower.displayName : t.filter.allGrowers,
        growerFarms.length,
      ),
    },
    ...growerFarms.map((f) => ({
      value: f.id,
      label: f.name,
      detail: grower
        ? t.filter.pucDetail(f.puc)
        : t.filter.pucGrowerDetail(f.puc, nameOfGrower(f.id)),
    })),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <div className="fw-filter-head">
        <span className="fw-label">
          {mode === 'recent' ? messages.dashboard.recent : t.filter.label}
        </span>
        {(filters.growerId || filters.farmId) && (
          <button
            type="button"
            className="fw-text-button"
            onClick={() => filters.set({ growerId: null, farmId: null })}
          >
            {t.filter.clear}
          </button>
        )}
      </div>
      <FilterCard
        rows={[
          {
            key: 'grower',
            icon: 'person-outline',
            label: t.filter.grower,
            value: grower?.displayName ?? t.filter.allGrowers,
            active: Boolean(grower),
            onClick: () => setPicker('grower'),
          },
          {
            key: 'farm',
            icon: 'location-outline',
            label: t.filter.farm,
            value: farm?.name ?? t.filter.allFarms,
            active: Boolean(farm),
            onClick: () => setPicker('farm'),
          },
        ]}
      />
      {!loading && <span className="fw-secondary">{scope + extra}</span>}
      {loading && <SkeletonRows count={3} />}
      {!loading && blocks.length === 0 && (
        <EmptyState icon="leaf-outline" title={messages.dashboard.emptyTitle}>
          {t.home.emptyBody}
        </EmptyState>
      )}
      {blocks.map((b) => (
        <div
          key={b.key}
          className="fw-group"
          style={{ gap: 'var(--space-2)', paddingTop: 'var(--space-3)' }}
        >
          <div className="fw-admin-group-head">
            <span className="fw-label fw-label--primary">{b.label.toUpperCase()}</span>
            <span className="fw-secondary fw-tnum">{t.filter.farms(b.farmCount)}</span>
          </div>
          {b.farms.map(({ farm: f, rows }) => (
            <div
              key={f.id}
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}
            >
              <div className="fw-admin-farm-head">
                <span className="fw-body-medium">{f.name}</span>
                <span className="fw-secondary fw-tnum">{t.filter.pucDetail(f.puc)}</span>
              </div>
              <div className="fw-rows">
                {rows.length === 0 && (
                  <div className="fw-secondary fw-admin-empty-row">{t.home.noEstimates}</div>
                )}
                {rows.map((e) => (
                  <EstimateRow key={e.id} estimate={e} showVarieties={mode === 'all'} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}

      <Picker
        open={picker === 'grower'}
        title={t.filter.chooseGrower}
        options={growerOptions}
        value={filters.growerId}
        closeLabel={messages.common.close}
        searchLabel={t.filter.search}
        searchPlaceholder={t.filter.searchGrower}
        noMatch={t.filter.noMatch}
        onClose={() => setPicker(null)}
        onPick={(id) => {
          const keepFarm =
            id && farm && growers?.find((g) => g.id === id)?.farmIds.includes(farm.id);
          filters.set({ growerId: id, farmId: keepFarm ? filters.farmId : null });
          setPicker(null);
        }}
      />
      <Picker
        open={picker === 'farm'}
        title={t.filter.chooseFarm}
        options={farmOptions}
        value={filters.farmId}
        closeLabel={messages.common.close}
        searchLabel={t.filter.search}
        searchPlaceholder={t.filter.searchFarm}
        noMatch={t.filter.noMatch}
        onClose={() => setPicker(null)}
        onPick={(id) => {
          const owner = id ? growers?.find((g) => g.farmIds.includes(id))?.id : undefined;
          filters.set({
            farmId: id,
            growerId: id ? (owner ?? filters.growerId) : filters.growerId,
          });
          setPicker(null);
        }}
      />
    </div>
  );
}
